import { useCreateRecord } from "../../../api/useCreateRecord";
import { useDeleteRecord } from "../../../api/useDeleteRecord";
import { useUpdateRecord } from "../../../api/useUpdateRecord";
import { useStore } from "../../../store/store";
import { sanitizeData } from "../../../utils/sanitizeFormData";
import { ServiceProvided, ServicesProvidedForm } from "./types";
import { SERVICES_PROVIDED_ENTITY } from "./utils";
import { FormatedFields } from "../../../types/fields.types";
import {
	formatDate,
	isDateAfter,
	isDateBefore,
	isDateBeforeOrSameDay,
	validateDateString,
} from "../../../utils/dateUtils";

export const useServicesProvidedFormHandler = (
	treatmentPrescriptionId: number | undefined,
	fieldsServicesProv: FormatedFields,
	servicesProvided: Array<ServiceProvided>,
	selectedServiceProvidedId: number | undefined
) => {
	const { notifications, appDialog } = useStore();
	const createRecord = useCreateRecord({ queryInvalidatePrefix: "treatment_prescription" });
	const updateRecord = useUpdateRecord({ queryInvalidatePrefix: "treatment_prescription" });
	const deleteRecord = useDeleteRecord({ queryInvalidatePrefix: "treatment_prescription" });

	const submitFn = async (formDataRaw: ServicesProvidedForm) => {
		const formData = {
			...formDataRaw,
			session_quantity: formDataRaw.session_periodicity === "not_applicable" ? 0 : formDataRaw.session_quantity,
		};

		const sanitizedData = sanitizeData(formData, fieldsServicesProv);
		if (sanitizedData.date_begin && !validateDateString(sanitizedData.date_begin)) {
			notifications.setNotification({
				isOpen: true,
				message: "Data de início inválida. Formato deve ser DD/MM/AAAA (ex: 01/01/2010).",
				severity: "warning",
			});
			return;
		}

		if (sanitizedData.date_end && !validateDateString(sanitizedData.date_end)) {
			notifications.setNotification({
				isOpen: true,
				message: "Data de fim inválida. Formato deve ser DD/MM/AAAA (ex: 01/01/2010).",
				severity: "warning",
			});
			return;
		}

		const selectedServiceProvided = servicesProvided.find((item) => item.id === selectedServiceProvidedId);

		if (formData.date_end) {
			if (
				(selectedServiceProvided?.date_begin || formData.date_begin) &&
				isDateBeforeOrSameDay(formData.date_end, formData.date_begin ?? selectedServiceProvided?.date_begin)
			) {
				notifications.setNotification({
					isOpen: true,
					message: "Data de fim tem de ser superior à data de início.",
					severity: "warning",
				});
				return;
			}
		}

		let serviceProvidersOfSameService = servicesProvided.filter((item) => item.service.id === formData.service);
		if (selectedServiceProvidedId) {
			serviceProvidersOfSameService = serviceProvidersOfSameService.filter(
				(item) => item.id !== selectedServiceProvidedId
			);
		}

		const newDateBegin = formData.date_begin ?? selectedServiceProvided?.date_begin;
		const newDateEnd = formData.date_end;

		if (newDateBegin) {
			const overlappingServiceProvided = serviceProvidersOfSameService.find((item) => {
				const beginsBeforeExistingEnds = !item.date_end || isDateBefore(newDateBegin, item.date_end);
				const endsAfterExistingBegins = !newDateEnd || isDateAfter(newDateEnd, item.date_begin);
				const hasOverlap = beginsBeforeExistingEnds && endsAfterExistingBegins;
				return hasOverlap;
			});

			if (overlappingServiceProvided) {
				notifications.setNotification({
					isOpen: true,
					message: `As datas selecionadas sobrepõem-se a um registo existente deste serviço (${formatDate(
						overlappingServiceProvided.date_begin
					)} - ${overlappingServiceProvided.date_end ? formatDate(overlappingServiceProvided.date_end) : "sem data de fim"}).`,
					severity: "warning",
				});
				return;
			}
		}

		if (
			sanitizedData.session_periodicity !== "not_applicable" &&
			(sanitizedData.session_quantity === 0 || sanitizedData.session_quantity === "0")
		) {
			notifications.setNotification({
				isOpen: true,
				message: 'A periodicidade tem de ser "Não Aplicável" se a quantidade de sessões for 0.',
				severity: "warning",
			});
			return;
		}

		if (selectedServiceProvidedId) {
			const { treatmentId, treatmentPrescriptionId, ...updateData } = sanitizedData;
			try {
				await updateRecord.mutateAsync({
					data: updateData,
					entity: SERVICES_PROVIDED_ENTITY,
					id: selectedServiceProvidedId,
				});

				notifications.setNotification({
					isOpen: true,
					message: "Dados atualizados com sucesso.",
					severity: "success",
				});
				appDialog.toggleIsOpen();
		} catch (error: any) {
			const errorMessage = error?.response?.data?.errors?.[0]?.message ?? "Erro ao atualizar dados.";
				notifications.setNotification({
					isOpen: true,
				message: errorMessage,
					severity: "error",
				});
			}
			return;
		}

		try {
			await createRecord.mutateAsync({
				entity: SERVICES_PROVIDED_ENTITY,
				data: { ...sanitizedData, treatment_prescription: treatmentPrescriptionId },
			});
			notifications.setNotification({
				isOpen: true,
				message: "Registo criado com sucesso.",
				severity: "success",
			});
			appDialog.toggleIsOpen();
	} catch (error: any) {
		const errorMessage = error?.response?.data?.errors?.[0]?.message ?? "Erro ao criar registo.";
			notifications.setNotification({
				isOpen: true,
			message: errorMessage,
				severity: "error",
			});
		}
	};

	const handleDelete = async (serviceProvidedId: number) => {
		const confirm = window.confirm("Tem a certeza que quer apagar este registo por completo?");
		if (!confirm) return;
		await deleteRecord.mutateAsync({ id: serviceProvidedId, entity: SERVICES_PROVIDED_ENTITY });
		appDialog.toggleIsOpen();
	};
	return { submitFn, handleDelete };
};
